import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { UseQueryResult } from '@tanstack/react-query/build/modern'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useBranding } from '../branding'

import { CurrentUser, currentUserParser } from '../models/CurrentUser'

import * as api from './api'
import useInvalidate from './useInvalidate'

interface Session {
  user?: CurrentUser
  isLoading: boolean
  isAuthorized: boolean
  invalidate: () => void
  logout: () => Promise<void>
  assumeClient: (clientUuid: string) => Promise<void>
}

const sessionContext = React.createContext<Session>({
  isLoading: true,
  isAuthorized: false,
  invalidate: () => {},
  logout: () => Promise.resolve(),
  assumeClient: (_clientUuid: string) => Promise.resolve(),
})

export const SessionProvider: React.FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  const { switchBranding } = useBranding()

  const queryClient = useQueryClient()
  const invalidateEntities = useInvalidate()

  const [current, setCurrent] = useState<any>({ user: null, isAuthorized: false, isLoading: true })

  const queryResultRef = useRef<any>(null)
  const userResult: UseQueryResult<CurrentUser, Error> = useQuery({
    queryKey: ['session'],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: 'always',
    queryFn: async () => {
      const fetched = currentUserParser.parse(await api.get('user/current'))
      if (JSON.stringify(queryResultRef.current) !== JSON.stringify(fetched)) {
        queryResultRef.current = fetched
      }
      return queryResultRef.current
    },
  })

  const isAuthorized = Boolean(userResult.isFetched && userResult.data && !userResult.error)
  const isLoading = !userResult.isFetched

  // When the user has changed, reload all data (except the user itself, since we just loaded that.)
  useEffect(() => {
    ;(async () => {
      if (userResult.data && isAuthorized) {
        await queryClient.invalidateQueries({
          predicate: (q) => q.queryKey[0] !== 'session',
        })
        invalidateEntities.groups()
        invalidateEntities.users()
        invalidateEntities.courses()
      }
      setCurrent({ user: userResult.data, isAuthorized, isLoading })
      switchBranding(userResult.data?.branding)
    })()
  }, [userResult.data, isAuthorized, isLoading])

  // When anyone notices that the session might be gone, they can call this function to make us check.
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['session'] })
  }, [queryClient])

  const logout = useCallback(async () => {
    try {
      await api.get('logout')
    } catch (err) {
      // Just in case - logout should always succeed
    }
    await queryClient.resetQueries()
  }, [invalidate])

  const assumeClient = useCallback(
    async (clientUuid: string) => {
      try {
        await api.post(`client/switch/${clientUuid}`, {})
        invalidate()
      } catch (err) {
        console.log(err)
      }
    },
    [invalidate]
  )

  const context = useMemo(
    () => ({
      user: current.user,
      isLoading: current.isLoading,
      isAuthorized: current.isAuthorized,
      invalidate,
      logout,
      assumeClient,
    }),
    [current, invalidate, logout]
  )

  return <sessionContext.Provider value={context}>{children}</sessionContext.Provider>
}

export const useSession = () => useContext(sessionContext)
